import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { UnknownAction } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { setOrganization, setOrganizations } from "../../model/app/actions";
import { login, setMe, setUser } from "../../model/oidc/actions";
import { Me, User } from "../../types/api-schema";
import { BROADCAST_MSG_LOGIN, LS_ORGANIZATION_KEY } from "../constants";
import fetchMe from "../fetchMe";
import fetchUserInfo from "../fetchUserInfo";
import { getAdminOrgOrConfig } from "../geopsAdminUtils";
import handleSessionExpiry from "../handleSessionExpiry";

import useAppSelector from "./useAppSelector";

export default function useAuthenticate(): {
  isAuthComplete: boolean;
  isAuthenticated: boolean;
} {
  const dispatch =
    useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  const ssoConfigsUrl = useAppSelector((state) => {
    return state.app.ssoConfigsUrl;
  });
  const userInfoUrl = useAppSelector((state) => {
    return state.app.userInfoUrl;
  });
  const meUrl = useAppSelector((state) => {
    return state.app.meUrl;
  });
  const apiUrl = useAppSelector((state) => {
    return state.app.apiUrl;
  });
  const broadcast = useAppSelector((state) => {
    return state.oidc.broadcast;
  });
  const [userInfo, setUserInfo] = useState<null | User>(null);
  const [meInfo, setMeInfo] = useState<Me | null>(null);
  const [isAuthComplete, setIsAuthComplete] = useState(false);
  const [isUserInfoComplete, setIsUserInfoComplete] = useState(false);
  const [isMeComplete, setIsMeComplete] = useState(false);

  useEffect(() => {
    const abortCtrl = new AbortController();
    const getData = async () => {
      const data = await fetchUserInfo(userInfoUrl, {
        signal: abortCtrl.signal,
      });
      setUserInfo(data);
      setIsUserInfoComplete(true);
    };
    getData();

    return () => {
      abortCtrl.abort();
    };
  }, [ssoConfigsUrl, userInfoUrl, meUrl]);

  useEffect(() => {
    // Keycloak: send the user to login once the session ended
    if (meInfo?.auth_provider !== "keycloak" || !apiUrl) {
      return undefined;
    }
    handleSessionExpiry(apiUrl, () => {
      dispatch(login());
    });
    return () => {
      handleSessionExpiry(apiUrl, null);
    };
  }, [apiUrl, dispatch, meInfo]);

  useEffect(() => {
    const abortCtrl = new AbortController();
    const getData = async () => {
      const data = await fetchMe(meUrl, {
        signal: abortCtrl.signal,
      });
      setMeInfo(data);
      setIsMeComplete(true);
    };
    getData();

    return () => {
      abortCtrl.abort();
    };
  }, [ssoConfigsUrl, meUrl]);

  useEffect(() => {
    if (userInfo && broadcast) {
      broadcast.postMessage(BROADCAST_MSG_LOGIN);
    }
  }, [broadcast, userInfo]);

  useEffect(() => {
    const updateConfig = async () => {
      const userOrganizations = userInfo?.organizations || [];
      dispatch(setOrganizations(userOrganizations));

      const savedOrganizationSlug = localStorage.getItem(LS_ORGANIZATION_KEY);
      const adminOrgOrConfig = getAdminOrgOrConfig(
        null,
        userOrganizations,
        userInfo,
      );
      const currentOrganization =
        // Prioritize the organization saved in localStorage if the user still has access to it.
        userOrganizations.find((org) => {
          return org.slug === savedOrganizationSlug;
        }) ||
        // Fall back to the admin organization or the first available organization.
        userOrganizations.find((org) => {
          return org.slug === adminOrgOrConfig?.slug;
        }) ||
        userOrganizations[0];
      dispatch(setOrganization(currentOrganization));
      setIsAuthComplete(true);
    };

    if (isUserInfoComplete) {
      dispatch(setUser(userInfo));
    }
    if (isMeComplete) {
      dispatch(setMe(meInfo));
    }
    if (isUserInfoComplete && isMeComplete) {
      setIsAuthComplete(true);
      updateConfig();
    }
  }, [dispatch, isUserInfoComplete, isMeComplete, userInfo, meInfo]);

  return { isAuthComplete, isAuthenticated: !!userInfo || !!meInfo };
}
